// frontend/src/components/staff/MyScheduleView.js
//
// Workforce — SCHED-SLICE1-G staff schedule view (read-only).
//
// Route: /staff/my-schedule (role: ['staff', 'admin', 'massage_therapist'])
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §9 + §11.2 +
//              §11.3 + §18.7
// Story:       docs/backlog/SCHED-SLICE1-G-staff-schedule-view-ui.md
// Tech doc:    docs/architecture/workforce-scheduling-tech.md §"Staff
//              endpoints (view-as-staff)" + §"Real-time sync transport —
//              POLLING" (5-min cadence on /api/workforce/my-shifts)
//
// SCOPE — pure-additive new surface; no changes to existing pages or modals.
// READ-ONLY — no edit/create/drag affordances. Staff cannot mutate any shift
// from this surface. The BE response is RBAC-redacted (own shifts only;
// hourlyRate stripped) per tech doc §17.1.1.
//
// Polling:
//   - 5-minute cadence per Marcus tech doc §"Real-time sync transport".
//   - Visibility-aware via `useFocusedPoll` hook (pauses when tab hidden;
//     catches up on visibility resume + focus + online events).
//   - On polling-detected change, render "Schedule changed" banner per
//     visual spec §9.5 — does NOT auto-refresh; user clicks banner to
//     confirm intent.
//
// Banners:
//   - Gold "Shift starts in N minutes" — fires when next shift is within
//     60 min of "now". Dismissible per-session (re-appears next mount if
//     shift still hasn't started).
//   - Teal "Schedule changed" — fires when polling detects a different
//     shift signature than the last view. Clicking refetches.
//
// Cancelled-shift "Recently cancelled" section (SCHED-CANCELLED-VISUAL-STAFF):
//   - Consumes `data.cancelledShifts` from `GET /api/workforce/my-shifts`.
//   - Renders a collapsible "Recently cancelled" section BELOW the day-view
//     when at least one cancelled shift falls within the visible week.
//   - Hidden entirely when N=0; default-collapsed when N>=1.
//   - Each card surfaces forensic detail inline (when + role + cancelled
//     timestamp + cancelledBy + reason) so staff does not need a drill-down
//     modal. Defensive on missing cancelReason / cancelledBy / cancelledAt
//     per visual spec §5.
//   - BE Shift serializer currently emits ONLY `cancelledAt` on the wire
//     (no `cancelReason`, no `cancelledBy`) — same constraint flagged on
//     PR #154. Defensive fallbacks keep the UI clean today; if a follow-up
//     BE story extends the serializer, the card lights up automatically.

import React from "react";
import { staffScheduleAPI } from "../../services/api";
import { useFocusedPoll } from "../../hooks/useFocusedPoll";
import StaffShiftDetailModal from "./StaffShiftDetailModal";
import {
  STUDIO_TZ_LABEL,
  buildCancelledShiftAriaLabel,
  buildShiftAriaLabel,
  buildShiftsSignature,
  buildWeekDays,
  computeMinutesUntilStart,
  computeWeekRange,
  computeWeekStart,
  countChangedShifts,
  filterCancelledShiftsForWeek,
  formatCancelledMetaLine,
  formatCancelledShiftWhenLine,
  formatRoleLabel,
  formatScheduleChangedBannerText,
  formatShiftStartsInText,
  formatShiftTimeRange,
  formatUpcomingShiftSubline,
  formatWeekRangeLabel,
  getRoleColor,
  groupShiftsByDay,
  isShiftPast,
  pickUpcomingShiftWithinWindow,
  stepWeek,
} from "./staffScheduleUtils";
import "./MyScheduleView.css";

// Poll cadence per Marcus tech doc §"Real-time sync transport — POLLING".
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// "Shift starts in N minutes" banner window — purely client-side per tech
// doc §"'Shift starts in 1 hour' reminder — DEFERRED to Slice 2".
const UPCOMING_WINDOW_MINUTES = 60;

const MyScheduleView = () => {
  // ---- URL/derived state ---------------------------------------------------
  // Week navigation is local state — the route does not encode weekStart in
  // the URL in Slice 1 (no story requirement). Today is the default.
  const [weekStart, setWeekStart] = React.useState(() => computeWeekStart());

  // ---- Data state ----------------------------------------------------------
  const [shifts, setShifts] = React.useState([]);
  // Cancelled shifts (SCHED-CANCELLED-VISUAL-STAFF) — segregated bucket from
  // `data.cancelledShifts`. Distinct from `shifts` (published only).
  const [cancelledShifts, setCancelledShifts] = React.useState([]);
  const [schedulePublishedForWeek, setSchedulePublishedForWeek] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [error, setError] = React.useState(null);

  // ---- "Recently cancelled" section ----------------------------------------
  // Default-collapsed when N>=1; resets to collapsed on week navigation per
  // visual spec §3.4. Hidden entirely when N===0 (see render branch).
  const [cancelledSectionExpanded, setCancelledSectionExpanded] = React.useState(false);

  // ---- Banner state --------------------------------------------------------
  // "Schedule changed" banner — fires when polling detects shift-signature
  // change. Reset to 0 on user-confirmed refresh OR week change.
  const [pendingChanges, setPendingChanges] = React.useState(0);
  // Last-applied signature — what the user is currently looking at.
  const lastAppliedSigRef = React.useRef("[]");
  // Pending refetched shifts — held until the user clicks banner OR 5s idle.
  const pendingShiftsRef = React.useRef(null);
  const pendingPublishedRef = React.useRef(true);

  // "Shift starts in N minutes" banner — dismissible per-session. State
  // resets when the user navigates to a different week or leaves the page.
  const [dismissedUpcoming, setDismissedUpcoming] = React.useState(false);

  // ---- Detail modal state --------------------------------------------------
  const [selectedShift, setSelectedShift] = React.useState(null);

  // ---- "now" tick ----------------------------------------------------------
  // For banner countdown + past-shift opacity, tick once a minute so the UI
  // stays roughly accurate without re-rendering on every animation frame.
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Mirror the latest applied shifts in a ref so the polling change-check
  // can compare without re-triggering the fetch callback.
  const shiftsForChangeCheck = React.useRef([]);

  // ---- Fetch ---------------------------------------------------------------
  // Apply a freshly-fetched response. If `pendingMode` is true, hold the
  // new shifts in pendingShiftsRef and increment pendingChanges instead of
  // overwriting the displayed shifts (used for polling-detected change).
  //
  // Cancelled shifts (SCHED-CANCELLED-VISUAL-STAFF) are NEVER pending — the
  // "Schedule changed" banner is scoped to published-shift deltas per
  // visual spec §9.5. A new cancellation arriving via poll updates the
  // "Recently cancelled" section directly on next render; staff already
  // received the email notification for the cancellation, so a banner
  // here would be redundant.
  const applyResponse = React.useCallback((data, pendingMode) => {
    const next = Array.isArray(data && data.shifts) ? data.shifts : [];
    // Defensive: an older BE response shape (or a malformed payload) that
    // omits `cancelledShifts` entirely is treated as N=0 cancellations —
    // section hides silently rather than crashing. Per spec §4.3.
    const nextCancelled = Array.isArray(data && data.cancelledShifts)
      ? data.cancelledShifts
      : [];
    const publishedFlag = data && typeof data.schedulePublishedForWeek === "boolean"
      ? data.schedulePublishedForWeek
      : null;
    const sig = buildShiftsSignature(next);

    if (pendingMode) {
      const delta = countChangedShifts(
        // Reconstruct old shifts from currently-applied signature — we keep
        // the actual array in state, so use it directly.
        // (lastAppliedSigRef just guards us against double-counting.)
        shiftsForChangeCheck.current,
        next
      );
      if (delta > 0) {
        pendingShiftsRef.current = next;
        pendingPublishedRef.current = publishedFlag;
        setPendingChanges(delta);
      } else {
        // Signature changed but no per-shift delta (e.g., updatedAt-only
        // bumps that don't materially change anything). Don't show banner.
        pendingShiftsRef.current = null;
      }
      // Cancelled bucket updates inline regardless of pendingMode — staff
      // sees fresh cancellations on next render without a banner.
      setCancelledShifts(nextCancelled);
      return;
    }

    setShifts(next);
    setCancelledShifts(nextCancelled);
    if (publishedFlag !== null) {
      setSchedulePublishedForWeek(publishedFlag);
    } else {
      // No flag in response — default to "published" (treat as §11.2 if no
      // shifts). §11.3 fires only when BE explicitly signals
      // schedulePublishedForWeek === false.
      setSchedulePublishedForWeek(true);
    }
    shiftsForChangeCheck.current = next;
    lastAppliedSigRef.current = sig;
    pendingShiftsRef.current = null;
    pendingPublishedRef.current = publishedFlag;
    setPendingChanges(0);
  // Intentionally not depending on shifts itself — we manage via ref.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial + week-change fetch.
  React.useEffect(() => {
    let cancelled = false;
    const fetchInitial = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { from, to } = computeWeekRange(weekStart);
        const res = await staffScheduleAPI.listMyShifts({ from, to });
        if (cancelled) return;
        const data = res && res.data ? res.data : {};
        applyResponse(data, /* pendingMode */ false);
        setIsInitialLoad(false);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    // Reset banner state on week change.
    setPendingChanges(0);
    pendingShiftsRef.current = null;
    setDismissedUpcoming(false);
    // Reset "Recently cancelled" section to collapsed default on week change
    // per visual spec §3.4 — exception data; no persistent expanded state.
    setCancelledSectionExpanded(false);
    fetchInitial();
    return () => { cancelled = true; };
  }, [weekStart, applyResponse]);

  // Polling refetch — does NOT touch loading state (silent in background).
  const pollRefetch = React.useCallback(async () => {
    try {
      const { from, to } = computeWeekRange(weekStart);
      const res = await staffScheduleAPI.listMyShifts({ from, to });
      const data = res && res.data ? res.data : {};
      applyResponse(data, /* pendingMode */ true);
    } catch (_) {
      // Swallow — polling errors don't disrupt the displayed schedule.
      // The next visibility/focus event triggers another attempt.
    }
  }, [weekStart, applyResponse]);

  useFocusedPoll(pollRefetch, POLL_INTERVAL_MS, [weekStart], {
    enabled: !isInitialLoad,
  });

  // Auto-apply pending changes after 5s idle per visual spec §9.5.
  React.useEffect(() => {
    if (pendingChanges <= 0) return undefined;
    const t = setTimeout(() => {
      // Apply pending shifts now.
      if (pendingShiftsRef.current) {
        setShifts(pendingShiftsRef.current);
        if (pendingPublishedRef.current !== null) {
          setSchedulePublishedForWeek(pendingPublishedRef.current);
        }
        shiftsForChangeCheck.current = pendingShiftsRef.current;
        lastAppliedSigRef.current = buildShiftsSignature(pendingShiftsRef.current);
        pendingShiftsRef.current = null;
        pendingPublishedRef.current = null;
      }
      setPendingChanges(0);
    }, 5000);
    return () => clearTimeout(t);
  }, [pendingChanges]);

  // ---- Derived view data ---------------------------------------------------
  const weekDays = React.useMemo(
    () => buildWeekDays(weekStart, nowTick),
    [weekStart, nowTick]
  );
  const shiftsByDay = React.useMemo(() => groupShiftsByDay(shifts), [shifts]);
  const weekRangeLabel = React.useMemo(
    () => formatWeekRangeLabel(weekStart),
    [weekStart]
  );

  const upcomingShift = React.useMemo(
    () => pickUpcomingShiftWithinWindow(shifts, UPCOMING_WINDOW_MINUTES, nowTick),
    [shifts, nowTick]
  );
  const upcomingMinutes = upcomingShift
    ? computeMinutesUntilStart(upcomingShift, nowTick)
    : null;

  // Cancelled shifts filtered to the visible week (SCHED-CANCELLED-VISUAL-STAFF
  // visual spec §2.6 — week-scoped "Recently" semantic).
  const weekCancelledShifts = React.useMemo(
    () => filterCancelledShiftsForWeek(cancelledShifts, weekStart),
    [cancelledShifts, weekStart]
  );

  // ---- Handlers ------------------------------------------------------------
  const handlePrevWeek = () => setWeekStart((w) => stepWeek(w, -1));
  const handleNextWeek = () => setWeekStart((w) => stepWeek(w, 1));
  const handleToday = () => setWeekStart(computeWeekStart());

  const handleShiftClick = (shift) => setSelectedShift(shift);
  const handleCloseModal = () => setSelectedShift(null);

  const handleConfirmRefresh = () => {
    if (pendingShiftsRef.current) {
      setShifts(pendingShiftsRef.current);
      if (pendingPublishedRef.current !== null) {
        setSchedulePublishedForWeek(pendingPublishedRef.current);
      }
      shiftsForChangeCheck.current = pendingShiftsRef.current;
      lastAppliedSigRef.current = buildShiftsSignature(pendingShiftsRef.current);
      pendingShiftsRef.current = null;
      pendingPublishedRef.current = null;
    }
    setPendingChanges(0);
  };

  const handleDismissUpcoming = () => setDismissedUpcoming(true);

  const handleToggleCancelledSection = () =>
    setCancelledSectionExpanded((prev) => !prev);

  // ---- Render --------------------------------------------------------------
  const showEmptyNoShifts =
    !isLoading && !error && shifts.length === 0 && schedulePublishedForWeek;
  const showEmptyNoSchedule =
    !isLoading && !error && shifts.length === 0 && !schedulePublishedForWeek;
  const showGrid = !error && (isLoading || shifts.length > 0);
  const showUpcomingBanner = upcomingShift && !dismissedUpcoming;
  const showChangedBanner = pendingChanges > 0;

  return (
    <div className="my-schedule" data-testid="my-schedule-page">
      <header className="my-schedule__header">
        <h1 className="my-schedule__heading">My Schedule</h1>
      </header>

      {showUpcomingBanner ? (
        <div
          className="my-schedule__banner my-schedule__banner--upcoming"
          role="alert"
          data-testid="my-schedule-banner-upcoming"
        >
          <div
            className="my-schedule__banner-icon"
            aria-hidden="true"
          >
            {/* Alarm-clock glyph — visual spec §9.4 (24pt gold). */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#B28F61"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="13" r="8" />
              <path d="M12 9v4l2 2" />
              <path d="M5 3 2 6" />
              <path d="m22 6-3-3" />
            </svg>
          </div>
          <div className="my-schedule__banner-body">
            <p className="my-schedule__banner-title">
              {formatShiftStartsInText(upcomingMinutes || 0)}
            </p>
            <p className="my-schedule__banner-subtitle">
              {formatUpcomingShiftSubline(upcomingShift)}
            </p>
          </div>
          <button
            type="button"
            className="my-schedule__banner-dismiss"
            onClick={handleDismissUpcoming}
            aria-label="Dismiss shift reminder"
            data-testid="my-schedule-banner-upcoming-dismiss"
          >
            &times;
          </button>
        </div>
      ) : null}

      {showChangedBanner ? (
        <button
          type="button"
          className="my-schedule__banner my-schedule__banner--changed"
          role="status"
          onClick={handleConfirmRefresh}
          data-testid="my-schedule-banner-changed"
        >
          <div
            className="my-schedule__banner-icon"
            aria-hidden="true"
          >
            {/* Info-circle glyph — visual spec §9.5 (teal). */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1F5C63"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4" />
              <path d="M12 16h.01" />
            </svg>
          </div>
          <div className="my-schedule__banner-body">
            <p className="my-schedule__banner-title">
              {formatScheduleChangedBannerText(pendingChanges)}
            </p>
          </div>
        </button>
      ) : null}

      <nav
        className="my-schedule__weeknav"
        aria-label="Week navigation"
      >
        <button
          type="button"
          className="my-schedule__weeknav-btn"
          onClick={handlePrevWeek}
          aria-label="Previous week"
          data-testid="my-schedule-prev"
        >
          &lsaquo;
        </button>
        <div className="my-schedule__weeknav-label" data-testid="my-schedule-week-label">
          {weekRangeLabel}
        </div>
        <button
          type="button"
          className="my-schedule__weeknav-today"
          onClick={handleToday}
          data-testid="my-schedule-today"
        >
          Today
        </button>
        <button
          type="button"
          className="my-schedule__weeknav-btn"
          onClick={handleNextWeek}
          aria-label="Next week"
          data-testid="my-schedule-next"
        >
          &rsaquo;
        </button>
      </nav>

      {isLoading && isInitialLoad ? (
        <p className="my-schedule__loading" data-testid="my-schedule-loading">
          Loading your schedule...
        </p>
      ) : null}

      {error && !isLoading ? (
        <p className="my-schedule__error" role="alert" data-testid="my-schedule-error">
          Unable to load your schedule. Try refreshing the page.
        </p>
      ) : null}

      {showEmptyNoShifts ? (
        <div
          className="my-schedule__empty"
          data-testid="my-schedule-empty-no-shifts"
        >
          <div className="my-schedule__empty-icon" aria-hidden="true">
            {/* Calendar icon — visual spec §11.2 (48pt muted). */}
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#B0B0B0"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4" />
              <path d="M8 2v4" />
              <path d="M3 10h18" />
            </svg>
          </div>
          <p className="my-schedule__empty-title">No shifts scheduled this week.</p>
          <p className="my-schedule__empty-body">
            Check back later or contact your admin if you expected shifts here.
          </p>
        </div>
      ) : null}

      {showEmptyNoSchedule ? (
        <div
          className="my-schedule__empty"
          data-testid="my-schedule-empty-no-schedule"
        >
          <div className="my-schedule__empty-icon" aria-hidden="true">
            {/* Hourglass icon — visual spec §11.3 (48pt muted). */}
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#B0B0B0"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 22h14" />
              <path d="M5 2h14" />
              <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
              <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
            </svg>
          </div>
          <p className="my-schedule__empty-title">Your schedule isn&apos;t ready yet.</p>
          <p className="my-schedule__empty-body">
            We&apos;ll notify you when your admin publishes the schedule for this week.
          </p>
        </div>
      ) : null}

      {showGrid && shifts.length > 0 ? (
        <section
          className="my-schedule__grid"
          aria-label="Weekly schedule grid"
          data-testid="my-schedule-grid"
        >
          {weekDays.map((day) => {
            const dayShifts = shiftsByDay.get(day.date) || [];
            return (
              <div
                key={day.date}
                className={
                  "my-schedule__day" +
                  (day.isToday ? " my-schedule__day--today" : "")
                }
                data-testid={`my-schedule-day-${day.date}`}
              >
                <div className="my-schedule__day-header">
                  <div className="my-schedule__day-name">{day.dayName}</div>
                  <div className="my-schedule__day-date">
                    <span className="my-schedule__day-month">{day.monthAbbr}</span>{" "}
                    <span className="my-schedule__day-number">{day.dayNumber}</span>
                  </div>
                </div>
                <div className="my-schedule__day-body">
                  {dayShifts.length === 0 ? (
                    <div
                      className="my-schedule__day-empty"
                      aria-hidden="true"
                      data-testid={`my-schedule-empty-cell-${day.date}`}
                    >
                      {/* Empty cell — staff cannot create shifts (AC Scenario 3). */}
                    </div>
                  ) : (
                    dayShifts.map((shift) => {
                      const role = getRoleColor(shift.roleCode);
                      const past = isShiftPast(shift, nowTick);
                      const label = formatRoleLabel(shift.roleCode);
                      return (
                        <button
                          key={shift._id}
                          type="button"
                          className={
                            "my-schedule__shift" +
                            (past ? " my-schedule__shift--past" : "")
                          }
                          style={{ background: role.bg, color: role.text }}
                          onClick={() => handleShiftClick(shift)}
                          aria-label={buildShiftAriaLabel(shift)}
                          data-testid={`my-schedule-shift-${shift._id}`}
                        >
                          <span className="my-schedule__shift-role">{label}</span>
                          <span className="my-schedule__shift-time">
                            {formatShiftTimeRange(shift.startTime, shift.endTime)}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </section>
      ) : null}

      {/* "Recently cancelled" section — SCHED-CANCELLED-VISUAL-STAFF.
          Hidden entirely when N=0 (visual spec §2.5 / §3.4) — no placeholder.
          Also hidden during loading + on fetch error (spec §4.1 / §4.2) so
          stale or absent data never surfaces a misleading section. */}
      {!isLoading && !error && weekCancelledShifts.length > 0 ? (
        <section
          className="my-schedule__cancelled"
          data-testid="my-schedule-cancelled-section"
        >
          <hr className="my-schedule__cancelled-divider" aria-hidden="true" />
          <button
            type="button"
            className="my-schedule__cancelled-header"
            onClick={handleToggleCancelledSection}
            aria-expanded={cancelledSectionExpanded}
            aria-controls="my-schedule-cancelled-list"
            aria-label={`Recently cancelled shifts (${weekCancelledShifts.length}) - click to ${
              cancelledSectionExpanded ? "collapse" : "expand"
            }`}
            data-testid="my-schedule-cancelled-toggle"
          >
            <span className="my-schedule__cancelled-header-label">
              Recently cancelled{" "}
              <span
                className="my-schedule__cancelled-header-count"
                data-testid="my-schedule-cancelled-count"
              >
                ({weekCancelledShifts.length})
              </span>
            </span>
            <span
              className={
                "my-schedule__cancelled-chevron" +
                (cancelledSectionExpanded
                  ? " my-schedule__cancelled-chevron--open"
                  : "")
              }
              aria-hidden="true"
              data-testid="my-schedule-cancelled-chevron"
            >
              {/* Right-chevron — rotates 90° when expanded via CSS. */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </span>
          </button>

          {cancelledSectionExpanded ? (
            <ul
              id="my-schedule-cancelled-list"
              className="my-schedule__cancelled-list"
              data-testid="my-schedule-cancelled-list"
            >
              {weekCancelledShifts.map((shift) => {
                const whenLine = formatCancelledShiftWhenLine(shift);
                const metaLine = formatCancelledMetaLine(shift);
                const reasonRaw =
                  typeof shift.cancelReason === "string"
                    ? shift.cancelReason.trim()
                    : "";
                const hasReason = reasonRaw.length > 0;
                return (
                  <li
                    key={shift._id || `${shift.startTime}-${shift.endTime}`}
                    className="my-schedule__cancelled-card"
                    aria-label={buildCancelledShiftAriaLabel(shift)}
                    data-testid={`my-schedule-cancelled-card-${
                      shift._id || `${shift.startTime}`
                    }`}
                  >
                    <div
                      className="my-schedule__cancelled-card-when"
                      data-testid="my-schedule-cancelled-when"
                    >
                      {whenLine}
                    </div>
                    <div
                      className="my-schedule__cancelled-card-meta"
                      data-testid="my-schedule-cancelled-meta"
                    >
                      {metaLine}
                    </div>
                    {hasReason ? (
                      <div
                        className="my-schedule__cancelled-card-reason"
                        title={reasonRaw}
                        data-testid="my-schedule-cancelled-reason"
                      >
                        Reason: {reasonRaw}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </section>
      ) : null}

      <p className="my-schedule__tz-footnote">
        All times shown in {STUDIO_TZ_LABEL} (Phoenix).
      </p>

      <StaffShiftDetailModal
        open={!!selectedShift}
        shift={selectedShift}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default MyScheduleView;
